////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: Full-width nav dropdown — hover (desktop) or tap (touch),       //
//           staggered items, focus-trapped, Esc closes (1.0.3.md §3)       //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////
//
//  Replaces the 1.0.1 full-screen <MenuOverlay /> wholesale, per this phase's
//  brief. A disclosure panel, not a modal dialog — the rest of the page stays
//  visible/scrollable behind it (deliberately, so "hover to preview, move away
//  to dismiss" reads naturally on desktop) — but it is still focus-trapped and
//  Esc-closable while open, since a sighted mouse user dismissing it by moving
//  away is not available to a keyboard/AT user; Header.tsx guarantees Esc
//  returns focus to the trigger button it renders.
//
////////////////////////////////////////////////////////////////////////////////

import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { NavLink } from 'react-router';

import { useReducedMotion } from '@/hooks/useReducedMotion';
import { fadeRise, reduceVariants, staggerGroup } from '@/lib/motion';
import { NAV_ITEMS } from './Nav';
import { ThemeToggle } from './ThemeToggle';

export interface MenuDropdownProps {
  id: string;
  open: boolean;
  onClose: () => void;
  /** True only when the panel was opened by an explicit activation (click, Enter,
   * Space) rather than by the desktop hover affordance. Gates BOTH the
   * move-focus-into-the-panel effect and the Tab trap: a hover-opened panel that
   * grabbed focus would yank the caret out from under a keyboard user who merely
   * swept the pointer across the header, and trapping Tab inside a panel nobody
   * asked to open strands them there. */
  focusOnOpen: boolean;
}

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function MenuDropdown ({ id, open, onClose, focusOnOpen }: MenuDropdownProps) {
  const reducedMotion = useReducedMotion();
  const panelRef = useRef<HTMLElement>(null);

  // Esc-to-close + Tab focus trap — both genuine synchronisation with an external
  // system (the keyboard/document), scoped to while the panel is actually open.
  // Esc applies whenever open; the Tab trap only once focus has deliberately been
  // placed inside (see `focusOnOpen`).
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !focusOnOpen || !panelRef.current) return;

      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      const first = focusable.at(0);
      const last = focusable.at(-1);
      if (!first || !last) return;

      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, focusOnOpen]);

  // Moves focus into the panel when it was opened deliberately — a keyboard user
  // who just activated the burger expects focus to land inside what they opened.
  // Deliberately NOT done for a hover-open: stealing focus on pointer-over is a
  // WCAG 3.2.1 (On Focus) style surprise and would fight the user's caret.
  useEffect(() => {
    if (open && focusOnOpen) panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
  }, [open, focusOnOpen]);

  const panelVariants = reduceVariants(fadeRise, reducedMotion);
  const listVariants = reduceVariants(staggerGroup, reducedMotion);
  const itemVariants = reduceVariants(fadeRise, reducedMotion);

  return (
    <AnimatePresence>
      {open && (
        // <nav>, not a bare <div> — components/chrome/Nav.tsx (which carried the
        // site's only `<nav aria-label="Primary">` landmark) is no longer rendered
        // anywhere after this phase replaced the header, so without this the public
        // site shipped with NO navigation landmark at all: screen-reader users lost
        // "jump to navigation" entirely. Same label as the Nav.tsx it supersedes.
        <motion.nav
          id={id}
          aria-label="Primary"
          ref={panelRef}
          className="glass-tile absolute inset-x-0 top-full z-40 mx-4 mt-2 sm:mx-6"
          variants={panelVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <motion.ul
            className="flex flex-col gap-2 p-6 text-xl sm:flex-row sm:items-center sm:justify-center sm:gap-10"
            variants={listVariants}
            initial="hidden"
            animate="visible"
          >
            {NAV_ITEMS.map((item) => (
              <motion.li key={item.to} variants={itemVariants}>
                <NavLink
                  to={item.to}
                  onClick={onClose}
                  className={({ isActive }) =>
                    isActive ? 'font-medium text-ink' : 'text-ink/80 hover:text-ink'
                  }
                >
                  {item.label}
                </NavLink>
              </motion.li>
            ))}
            {/* MARK — the header's brief (1.0.3.md §3) is explicit that the
                transparent bar itself carries "wordmark left, burger right ...
                Nothing else" — ThemeToggle (a real, pre-existing control) has to
                live somewhere, and the dropdown it used to sit beside is the
                least surprising new home for it. Flagging for ratification
                rather than silently dropping the control. */}
            <motion.li variants={itemVariants} className="sm:ml-4">
              <ThemeToggle />
            </motion.li>
          </motion.ul>
        </motion.nav>
      )}
    </AnimatePresence>
  );
}
